import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { usePerformAdminActionMutation, useSetDueDateMutation } from 'store/services/case/caseApi';
import { CaseStatus, AdminActionType } from 'types/interfaces/case';
import './AdminActionsPanel.css';

interface Props {
  caseId: string;
  caseStatus: CaseStatus;
}

const AdminActionsPanel: React.FC<Props> = ({ caseId, caseStatus }) => {
  const [showDueDatePicker, setShowDueDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [performAction, { isLoading: isPerformingAction }] = usePerformAdminActionMutation();
  const [setDueDate, { isLoading: isSettingDueDate }] = useSetDueDateMutation();

  const isResolved = caseStatus === CaseStatus.RESOLVED;

  const handleAction = async (
    actionType:
      | AdminActionType.COMPLETE_TRADE
      | AdminActionType.CANCEL_TRADE
      | AdminActionType.REFUND_BOTH
      | AdminActionType.NO_ACTION
  ) => {
    const actionLabels = {
      [AdminActionType.COMPLETE_TRADE]: 'complete this trade',
      [AdminActionType.CANCEL_TRADE]: 'cancel this trade',
      [AdminActionType.REFUND_BOTH]: 'refund both parties',
      [AdminActionType.NO_ACTION]: 'resolve this case with no action'
    };

    const confirmMessage = `Are you sure you want to ${actionLabels[actionType]}? This action cannot be undone.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await performAction({
        caseId,
        actionType
      }).unwrap();

      toast.success(`Action completed: ${actionType.replace(/_/g, ' ').toLowerCase()}`);
    } catch (error: any) {
      console.error('Failed to perform action:', error);
      toast.error(error.data?.message || 'Failed to perform action. Please try again.');
    }
  };

  const handleSetDueDate = async () => {
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }

    const dueDate = new Date(selectedDate);
    const now = new Date();

    // Validate future date
    if (dueDate <= now) {
      toast.error('Due date must be in the future');
      return;
    }

    try {
      await setDueDate({
        caseId,
        dueDate: dueDate.toISOString()
      }).unwrap();

      toast.success(`Due date set to ${dueDate.toLocaleDateString()}`);
      setShowDueDatePicker(false);
      setSelectedDate('');
    } catch (error: any) {
      console.error('Failed to set due date:', error);
      toast.error(error.data?.message || 'Failed to set due date. Please try again.');
    }
  };

  const isActionDisabled = isResolved || isPerformingAction || isSettingDueDate;

  return (
    <div className="admin-actions-panel">
      <h2 className="panel-title">Admin Actions</h2>

      {isResolved && (
        <div className="resolved-notice">
          This case has been resolved. No further actions can be taken.
        </div>
      )}

      <div className="case-action-buttons">
        <button
          className="case-action-button case-action-complete"
          onClick={() => handleAction(AdminActionType.COMPLETE_TRADE)}
          disabled={isActionDisabled}
          title="Mark the trade as completed and credit the sender"
        >
          <span className="case-action-icon">✓</span>
          Complete Trade
        </button>

        <button
          className="case-action-button case-action-cancel"
          onClick={() => handleAction(AdminActionType.CANCEL_TRADE)}
          disabled={isActionDisabled}
          title="Cancel the trade (no points transferred)"
        >
          <span className="case-action-icon">✕</span>
          Cancel Trade
        </button>

        <button
          className="case-action-button case-action-refund"
          onClick={() => handleAction(AdminActionType.REFUND_BOTH)}
          disabled={isActionDisabled}
          title="Cancel trade and refund both parties"
        >
          <span className="case-action-icon">↻</span>
          Refund Both Parties
        </button>

        <button
          className="case-action-button case-action-no-action"
          onClick={() => handleAction(AdminActionType.NO_ACTION)}
          disabled={isActionDisabled}
          title="Resolve the case without taking any action on the trade"
        >
          <span className="case-action-icon">○</span>
          No Action
        </button>

        <button
          className="case-action-button case-action-due-date"
          onClick={() => setShowDueDatePicker(!showDueDatePicker)}
          disabled={isActionDisabled}
          title="Set a due date for case follow-up"
        >
          <span className="case-action-icon">📅</span>
          Set Due Date
        </button>
      </div>

      {showDueDatePicker && !isResolved && (
        <div className="due-date-picker-section">
          <h3 className="picker-title">Set Due Date</h3>
          <p className="picker-description">
            The case will be marked as resolved and reopened automatically on the selected date.
          </p>
          <div className="picker-inputs">
            <input
              type="date"
              className="date-input"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />
            <div className="picker-actions">
              <button
                className="picker-button picker-confirm"
                onClick={handleSetDueDate}
                disabled={!selectedDate || isSettingDueDate}
              >
                {isSettingDueDate ? 'Setting...' : 'Confirm'}
              </button>
              <button
                className="picker-button picker-cancel"
                onClick={() => {
                  setShowDueDatePicker(false);
                  setSelectedDate('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="action-info">
        <h3 className="info-title">Action Details:</h3>
        <ul className="info-list">
          <li>
            <strong>Complete Trade:</strong> Marks trade as completed, credits sender (minus fees), and resolves case
          </li>
          <li>
            <strong>Cancel Trade:</strong> Cancels the trade, no points transferred, resolves case
          </li>
          <li>
            <strong>Refund Both:</strong> Cancels trade, refunds receiver, credits sender (minus fees), resolves case
          </li>
          <li>
            <strong>No Action:</strong> Resolves the case without affecting the trade status
          </li>
          <li>
            <strong>Set Due Date:</strong> Resolves case and automatically reopens it on the selected date
          </li>
        </ul>
      </div>
    </div>
  );
};

export default AdminActionsPanel;
